/* eslint-disable camelcase */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { AxiosResponse } from 'axios';
import Show from '../common/Show';
import Tooltip from '../common/Tooltip';
import { TFreelancerData, TPageNotification, TUser } from '../../types';
import FreelancerDetailsAgentNotes from './freelancerDetailsAgentNotes';
import FreelancerDetailsQuickActions from './FreelancerDetailsQuickActions';


export type TFreelancerDetailsHeaderProps = {
    user: TUser;
    isAgent: boolean;
    isClient: boolean;
    freelancerData: TFreelancerData;
    setFreelancerData: (data: TFreelancerData) => void;
    stats: {
        content?: any;
        hasMoreItems?: boolean;
        loading?: boolean;
        meta?: { status_code: number; status: string };
    };
    onPhotoUpload: (event: File | null) => void;
    onSave: (fields: any) => Promise<AxiosResponse>;
    notification: TPageNotification;
};

export type TFreelancerDetailsHeaderState = {
    showSMS: boolean;
    showNotification: boolean;
};


export const WorkerAgency = (props: { agencyName: string; logo: string }) => (
    <Tooltip tooltip={`Worker managed by ${props.agencyName}`}>
        <div className="agency-logo">
            <img
                className="agency-logo__photo"
                src={props.logo || '/static/images/default_profile.png'}
                alt="logo"
            />
        </div>
    </Tooltip>
);

class FreelancerDetailsHeader extends Component<
    TFreelancerDetailsHeaderProps,
    TFreelancerDetailsHeaderState
> {
    state: TFreelancerDetailsHeaderState = {
        showSMS: false,
        showNotification: false,
    };

    handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        this.props.onPhotoUpload(e.target.files[0]);
    }

    // eslint-disable-next-line consistent-return
    renderAgencyIcon() {
        const { freelancerData, user, isAgent } = this.props;
        if (isAgent) {
            const { managing_agency } = freelancerData;
            if (!managing_agency) {
                return null;
            }
            const isExternalWorker = managing_agency.id !== user.agency.id;
            return isExternalWorker ? (
                <WorkerAgency
                    agencyName={managing_agency.company_name}
                    logo={managing_agency.logo}
                />
            ) : null;
        }
    }

    render() {
        const { freelancerData, stats, isAgent } = this.props;

        const photoThumbnail = freelancerData.photo_thumbnail_large
            ? freelancerData.photo_thumbnail_large
            : '/static/images/default_profile.png';
        return (
            <div className="freelancer-details-header">
                {isAgent && (
                    <div className="freelancer-details-header__quick-actions">
                        <FreelancerDetailsQuickActions
                            freelancerData={freelancerData}
                            notification={this.props.notification}
                            onSave={this.props.onSave}
                            setFreelancerData={this.props.setFreelancerData}
                            user={this.props.user}
                        />
                    </div>
                )}
                <div className="freelancer-details-header__avatar">
                    <div className="freelancer-avatar">
                        <div className="freelancer-avatar__photo">
                            <img src={photoThumbnail} alt="freelancer" />
                            {isAgent && (
                                <label className="freelancer-avatar__edit-btn">
                                    <i className="icon icon-pencil-3" />
                                    <input
                                        type="file"
                                        onChange={this.handleFileInputChange.bind(
                                            this,
                                        )}
                                        className="freelancer-avatar__file-input"
                                    />
                                </label>
                            )}
                            {this.renderAgencyIcon()}
                        </div>

                        <div className="freelancer-avatar__name">
                            {freelancerData.full_name}
                        </div>
                        {isAgent && freelancerData.unpublication_reason && (
                            <div className="badge-error">
                                {freelancerData.unpublication_reason}
                            </div>
                        )}
                        {isAgent && !!freelancerData.owning_agencies?.length && (
                            <div className="freelancer-avatar__owning-branch">
                                Owning branch{freelancerData.owning_agencies?.length > 1 ? 'es' : ''}:
                                {' '}
                                {freelancerData.owning_agencies?.join(', ')}
                            </div>
                        )}
                    </div>
                </div>
                {isAgent && (
                    <FreelancerDetailsAgentNotes
                        agentNotes={this.props.freelancerData.agent_notes}
                        onSave={this.props.onSave}
                    />
                )}
                <div className="freelancer-details-header__stats">
                    <div className="freelancer-stats">
                        <div className="freelancer-stats__inner">
                            <div className="freelancer-stats__items">
                                <div className="freelancer-stats__item">
                                    <div className="freelancer-stats__icon">
                                        <i className="icon icon-cog" />
                                    </div>
                                    <div className="freelancer-stats__title">
                                        Service
                                    </div>
                                    <div className="freelancer-stats__value">
                                        {freelancerData.service_key}
                                    </div>
                                </div>
                                {this.props.isClient ? null : (
                                    <div className="freelancer-stats__item">
                                        <div className="freelancer-stats__icon">
                                            <i className="icon icon-person-2" />
                                        </div>
                                        <div className="freelancer-stats__title">
                                            Published
                                        </div>
                                        <div className="freelancer-stats__value">
                                            {freelancerData.published
                                                ? 'Yes'
                                                : 'No'}
                                        </div>
                                    </div>
                                )}
                                <div className="freelancer-stats__item">
                                    <div className="freelancer-stats__icon">
                                        <i className="icon icon-ratings" />
                                    </div>
                                    <div className="freelancer-stats__title">
                                        Rating
                                    </div>
                                    <div className="freelancer-stats__value">
                                        {freelancerData.average_score
                                            ? freelancerData.average_score
                                            : 'N/A'}
                                    </div>
                                </div>
                                <div className="freelancer-stats__item">
                                    <div className="freelancer-stats__icon">
                                        <i className="icon icon-stopwatch" />
                                    </div>
                                    <div className="freelancer-stats__title">
                                        Punctuality Rate
                                    </div>
                                    <div className="freelancer-stats__value">
                                        {stats.content &&
                                        stats.content.punctuality ? (
                                            <div className="freelancer-stats__value">
                                                {`${(
                                                    stats.content.punctuality *
                                                    100
                                                ).toFixed(1)}%`}
                                            </div>
                                        ) : (
                                            <div className="freelancer-stats__value">
                                                N/A
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <Show if={this.props.isAgent}>
                                    <div className="freelancer-stats__item">
                                        <div className="freelancer-stats__icon">
                                            <i className="icon icon-speaker" />
                                        </div>
                                        <div className="freelancer-stats__title">
                                            Strikes
                                        </div>
                                        <div className="freelancer-stats__value">
                                            {freelancerData.strikes_count}
                                        </div>
                                    </div>
                                </Show>
                                <Show if={this.props.isAgent}>
                                    <div className="freelancer-stats__item">
                                        <div className="freelancer-stats__icon">
                                            <i className="icon icon-pointer" />
                                        </div>
                                        <div className="freelancer-stats__title">
                                            BuzzPoints
                                        </div>
                                        <div className="freelancer-stats__value">
                                            {freelancerData.total_points}
                                        </div>
                                    </div>
                                </Show>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

type TState = {
    user: TUser;
};

const mapStateToProps = (state: TState) => ({
    user: state.user,
});

export default connect(mapStateToProps)(FreelancerDetailsHeader);
